"use client";

import { BotMessageSquareIcon } from "@/components/ui/bot-message-square";
import { useGlobalResponseNotifications } from "@/hooks/use-global-response-notifications";
import { useSession } from "@/hooks/use-session";
import { usePathname, useSearchParams } from "next/navigation";
import { toast } from "sonner";

/**
 * Mounted once in the root layout — not inside home-client.tsx — precisely
 * so this outlives navigation to /chats, /projects, and every other
 * top-level route. It used to live in home-client.tsx, which is the page
 * component for "/" only: navigating to "/chats" unmounted it (and its
 * Realtime subscription) entirely, so a turn finishing while the owner was
 * on the chats list could never toast — the exact case this exists for.
 *
 * activeChatId/activeProjectChatId are derived from the URL rather than
 * threaded down from home-client.tsx's own state, since that state simply
 * isn't reachable from here — but it's the same source of truth
 * home-client.tsx's own deep-link effects read from, and only "/" ever
 * shows a chat at all.
 */
export function GlobalNotifications({ userId }: { userId: string | null }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const onHome = pathname === "/";
  const activeChatId = onHome ? searchParams.get("chat") : null;
  const activeProjectChatId = onHome ? searchParams.get("projectChat") : null;

  useGlobalResponseNotifications({
    userId,
    activeChatId,
    activeProjectChatId,
    // Plain toast() rather than toast.success() — richColors would render
    // this in success-green, which reads as a status/confirmation banner
    // rather than a quiet "something happened elsewhere" nudge. No message
    // preview in the body: it's raw message content, which can carry
    // markdown syntax (bold, lists, ...) that a plain toast string can't
    // render, so a fixed line avoids ever showing literal "**" characters.
    //
    // Icon and text are composed into one `message` node instead of using
    // sonner's `icon` option — that option's slot assumes the small
    // default icons' footprint (see ui/sonner.tsx), and our larger
    // badge-wrapped icon overlapped the title text under it. Laying both
    // out ourselves in a flex row sidesteps that entirely.
    onResponseReady: ({ chatName }) => {
      toast(
        <div className="flex items-center gap-3">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
            <BotMessageSquareIcon className="text-foreground" size={16} />
          </span>
          <span className="text-sm text-foreground">
            Your response for chat {chatName} is ready.
          </span>
        </div>,
        {
          duration: Infinity,
          closeButton: true,
        }
      );
    },
  });

  return null;
}

/** Resolves the signed-in user id for GlobalNotifications — kept separate
 *  so the root layout's Suspense boundary only has to wrap this tiny piece,
 *  not force the rest of the tree into it. */
export function GlobalNotificationsGate() {
  const session = useSession();
  const userId = session?.user?.id ?? null;
  if (!userId) return null;
  return <GlobalNotifications userId={userId} />;
}
